import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import type { Disposable } from '../../core/Event';
import type { SlotGameModel } from '../../model/slot/SlotGameModel';
import { BalanceDisplay } from '../../widgets/BalanceDisplay';
import { FreeSpinChip } from '../../widgets/FreeSpinChip';
import { BetSelector } from '../../widgets/slot/BetSelector';
import { IconButton } from '../../widgets/IconButton';
import { LoadoutStripHud } from '../../widgets/slot/LoadoutStripHud';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';
import type { SpinMode } from '../../logic/road/boardRun';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

/** The face over SPIN for each Royal Road mode (none for plain SPIN): the
 *  button art, made from the SPIN art so the disc and lettering match, and
 *  a drawn stand-in (base colour, lighter top) for when it isn't loaded.
 *  While the light lands the STOP art shows dimmed. */
const PLATES: Record<SpinMode, { art: string; label: string; fill: number; light: number } | null> = {
  spin: null,
  stop: { art: 'shared:button:stop_idle', label: 'STOP', fill: 0xd8324e, light: 0xff7a8e },
  landing: { art: 'shared:button:stop_idle', label: '…', fill: 0x6b5a7a, light: 0x9a88aa },
  collect: { art: 'shared:button:collect_idle', label: 'COLLECT', fill: 0x239a5a, light: 0x6fe0a0 },
  done: { art: 'shared:button:done_idle', label: 'DONE', fill: 0x6a3aa8, light: 0xa880e0 },
};

/** The SPIN button's size; its art fills this square. */
const SPIN_SIZE = 192;

/** The SPIN disc's coloured face sits inside its white rim: this radius
 *  (in the button's 192px space) covers the face and leaves the rim. */
const FACE_RADIUS = 67;
/** Widest a label may be across the face. */
const FACE_LABEL_WIDTH = 104;

/** iOS safe-area-inset-top in CSS px. The wallet pill (the take-win
 *  target) is anchored at {@code top: env(safe-area-inset-top)+4} in the
 *  DOM, so on notch / Dynamic Island devices the Pixi target must shift
 *  down by the same inset or the flying coins land above the pill. Probes
 *  the live env() value; 0 on web / headless. Consistent with the engine's
 *  other guarded browser reads (e.g. localStorage in ProfileStorage). */
export function safeAreaInsetTop(): number {
  if (typeof document === 'undefined') return 0;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const inset = probe.getBoundingClientRect().height;
  document.body.removeChild(probe);
  return inset;
}

// Producer 2026-05-29: the slot hint line ("bet N · SPIN or space ·
// tap a reel to HOLD · ⓘ paytable") is removed entirely. It was a
// desktop-era keyboard hint ("or space") that reads wrong on mobile
// and cluttered the bottom of the frame on camera.
//
// The Text node also no longer renders anything — its visibility is
// forced off at creation (see this.resultLabel.visible = false below),
// so every {@link setResultText} call (win messages, the "not enough
// coins for N" warning, the gamble offer label) is a no-op on screen.
// Producer brief 2026-06-04: the bottom-of-frame white text was
// reading as UI clutter against the painterly scene art and competing
// with the commissioned border + tray; better to lean on the toast
// + sting layer for moment-to-moment feedback.
const HINT_TEMPLATE = (_bet: number): string => '';

export interface SlotHudCallbacks {
  onSpin: () => void;
  onTogglePaytable: () => void;
  /** Tap on the gear icon — host should open a settings modal.
   *  Optional: when absent, the gear button is omitted from the row. */
  onOpenSettings?: () => void;
  /** Inline GAMBLE button (2026-06-04 brief). When set + the host
   *  flips {@link setGambleButtonVisible} true after a win, the
   *  player sees a smaller gold medallion to the right of SPIN.
   *  Tap → host opens the gamble overlay. */
  onGamble?: () => void;
  /** The player picked a bet on the BET pill (not a clamp to what they
   *  can afford), so the host can remember it. */
  onBetChosen?: (bet: number) => void;
}

export interface SlotHudOptions {
  /** Chip accent colour for the free-spin counter. Defaults to brand pink. */
  freeSpinChipAccent?: number;
  /** Glyph for the free-spin chip. Defaults to scatter target — should match
   * the theme's scatter symbol. */
  freeSpinChipGlyph?: string;
  /** F2.2 — onboarding-curriculum session number (UX Spec §4). When
   * {@code 1}, the BET selector hides entirely so the player can't toggle
   * away from the locked-at-25 bet (the model's bet ladder is collapsed
   * to a single rung upstream in {@link SlotDemoScene}). */
  onboardingSession?: number | null;
  /** Hide the balance counter at the top of the HUD. Used when the
   * parent scene owns the wallet display — e.g. the standalone slot
   * (where the React {@code BalancePill} overlay handles the top-right
   * wallet HUD) or the Wheel room's REEL_RACE phase (whose timer
   * header would collide with the slot's BALANCE label).
   *
   * <p>By default this ALSO hides the free-spin chip, since the chip
   * normally sits beneath the balance counter. Set
   * {@link keepFreeSpinChip} when the parent shows the wallet but
   * doesn't expose a free-spin counter — the standalone slot's case.
   */
  hideHudBalance?: boolean;
  /** When {@link hideHudBalance} is true, keep the free-spin chip
   * visible anyway. Used by the standalone slot scene: the React
   * BalancePill replaces the balance counter (which is wallet info)
   * but the free-spin counter is a slot-only readout that no other
   * UI layer exposes. Ignored when {@code hideHudBalance} is false. */
  keepFreeSpinChip?: boolean;
  /** Lock the BET selector + paint a small padlock badge on its coin
   *  glyph. Used by the multiplayer Reel Race where every spin runs
   *  at a fixed notional bet (the server clamps to
   *  {@code REEL_RACE_NOTIONAL_BET} regardless of what the client
   *  shows), so the player needs a visual cue that the value isn't
   *  tappable. Stacks with the onboarding-session-1 lock — either
   *  reason locks the pill. */
  lockBet?: boolean;
  /** When true, mount a {@link LoadoutStripHud} as a sibling of the
   *  BET / SPIN / INFO row, positioned to the LEFT of the
   *  INFO/SETTINGS cluster. Used by the multiplayer Reel Race so
   *  the player's 3-item loadout reads as a HUD pill rather than a
   *  DOM overlay. */
  showLoadoutStrip?: boolean;
}

/**
 * The HUD around the slot machine: balance counter (top centre), spin button,
 * bet selector, info button, and result-hint text below the spin button.
 *
 * <p>Subscribes directly to the {@link SlotGameModel} so labels stay in sync
 * without the host scene wiring intermediate callbacks. The scene only needs
 * to drive the visible state machine via {@link setSpinning} /
 * {@link setSpinButtonVisible} / {@link setResultText}.
 */
export class SlotHudComponent extends Container implements SceneComponent {
  private readonly balance: BalanceDisplay;
  private readonly freeSpinChip: FreeSpinChip;
  /** Producer audit Phase 2.1 — joined-up control bar. A single dark
   * plinth with a subtle gold trim sits behind the BET / SPIN / INFO
   * row so the three controls read as one widget rather than three
   * floating commissioned buttons. Per-biome painted plinths land
   * when the asset commissions return; this drawn version ships now
   * so the row stops feeling disconnected. */
  private readonly controlBarPlinth: Graphics;
  /** Pill background for the bet selector — drawn HERE instead of
   *  inside {@link BetSelector} so its gold border renders at world
   *  scale (same drawing path + same colors as the cluster pill).
   *  Without this, the bet pill border visibly thinned on small
   *  viewports because the BetSelector container is uniformly scaled
   *  by {@code hudScale}. Producer brief 2026-05-12. */
  private readonly betPillPlinth: Graphics;
  private readonly spinButton: PrimaryButton;
  /** Royal Road: a plate over SPIN reading STOP, COLLECT or DONE while a
   *  Lamp Run is on (see {@link setSpinMode}). */
  private readonly modePlate = new Container();
  private readonly modePlateArt = new Sprite();
  private readonly modePlateBg = new Graphics();
  private readonly modePlateText = new Text({
    text: '',
    style: { fontFamily: 'Fredoka, ' + FONT_FAMILY, fontSize: 44, fontWeight: '900', fill: 0xffffff, letterSpacing: 1,
      stroke: { color: 0x2a0a20, width: 6 }, dropShadow: { color: 0x000000, alpha: 0.35, distance: 2, blur: 2, angle: Math.PI / 2 } },
  });
  private spinMode: SpinMode = 'spin';
  /** Inline GAMBLE medallion to the right of SPIN. Replaces the modal-
   *  style GAMBLE/COLLECT buttons that {@link GambleFlowComponent}
   *  used to drop on top of the slot. Hidden by default; the host
   *  scene calls {@link setGambleButtonVisible}(true) once a win
   *  settles (provided {@code gambleEnabled} on the profile). */
  private readonly gambleButton: PrimaryButton | null;
  private readonly betSelector: BetSelector;
  private readonly infoButton: IconButton;
  /** Optional gear button — settings modal trigger. Constructed only
   *  when {@code callbacks.onOpenSettings} is provided so existing
   *  callers (engine-only test scenes) work unchanged. */
  private readonly settingsButton: IconButton | null;
  private readonly resultLabel: Text;
  /** Transient "Locked for multiplayer" tooltip bubble shown when the
   *  player taps the locked BET pill. Anchored ABOVE the pill (the
   *  result label sits at the canvas bottom edge, which is clipped /
   *  off-screen in the multiplayer Reel Race embed — see 2026-05-31
   *  fix). Built lazily on first show so non-locked HUDs pay nothing. */
  private lockedBetTooltip: Container | null = null;
  private readonly subscriptions: Disposable[] = [];
  private spinning = false;
  /** Last {@link SceneLayoutContext} handed to {@link layout}. Cached so
   *  the {@code bet-changed} subscription can re-run the layout when the
   *  player cycles the bet — otherwise the BET pill's plinth chrome
   *  (drawn here at world scale from {@link BetSelector#pillWidth}) stays
   *  frozen at the previous bet's width while the digits/coin inside
   *  resize. Reset to {@code null} until the host calls {@link layout}
   *  at least once. */
  private lastLayoutCtx: SceneLayoutContext | null = null;
  /** F2.2 — true when the bet is locked (currently only onboarding session 1).
   * Hides the BET pill and short-circuits its enable/disable toggling. */
  private readonly betLocked: boolean;
  /** Optional 3-slot loadout strip — multiplayer Reel Race. Mounted
   *  when {@link SlotHudOptions#showLoadoutStrip} is true. Sits to
   *  the LEFT of the INFO/SETTINGS cluster, sharing the same row. */
  readonly loadoutStrip: LoadoutStripHud | null;

  constructor(
    private readonly model: SlotGameModel,
    private readonly callbacks: SlotHudCallbacks,
    options: SlotHudOptions = {},
  ) {
    super();
    this.betLocked =
      options.onboardingSession === 1 || options.lockBet === true;
    this.balance = new BalanceDisplay(this.model.balance);
    this.freeSpinChip = new FreeSpinChip({
      accentColor: options.freeSpinChipAccent,
      glyph: options.freeSpinChipGlyph,
    });
    this.freeSpinChip.setCount(this.model.freeSpinsRemaining);
    this.spinButton = new PrimaryButton({
      label: 'SPIN',
      // Producer audit Phase 4 (slot-HUD redesign brief). SPIN is the
      // visual focal point of the HUD and overlaps the slot frame's
      // bottom edge. Sizing history: 96 → 128 → 192 (1.5× the prior
      // disc per the redesign follow-up). The painted disc dominates
      // the spread-row layout and lifts slightly above {@code
      // spinButtonY} (see {@link layout}) so it sits visibly into the
      // wood frame's bottom edge rather than just touching it.
      height: SPIN_SIZE,
      width: SPIN_SIZE,
      cornerRadius: 96,
      fillActive: 0xff6bd6,
      fillHover: 0x6b3d77,
      textColor: 0xffffff,
      // Commissioned full-button reskin — pink "SPIN" disc with
      // pressed + disabled state textures. PrimaryButton swaps to
      // image-mode rendering when imageIdleAlias resolves; falls
      // back to the drawn pill + iconAlias glyph otherwise (e.g.
      // engine-only test scenes).
      iconAlias: 'shared:icon:spin',
      imageIdleAlias: 'shared:button:spin_idle',
      imagePressedAlias: 'shared:button:spin_pressed',
      imageDisabledAlias: 'shared:button:spin_disabled',
      onTap: () => this.callbacks.onSpin(),
    });

    // Inline GAMBLE medallion — half the size of SPIN, same painterly
    // family (commissioned chunky-3D gold button with the GAMBLE
    // wordmark). Hidden by default; flipped visible by
    // {@link setGambleButtonVisible} when the scene's win flow lands
    // with pendingWinnings > 0 and gambleEnabled on the profile.
    if (callbacks.onGamble) {
      this.gambleButton = new PrimaryButton({
        label: 'GAMBLE',
        height: 96,
        width: 96,
        cornerRadius: 48,
        fillActive: 0xffc940,
        fillHover: 0xc89030,
        textColor: 0x3d2400,
        imageIdleAlias: 'shared:button:gamble_idle',
        onTap: () => this.callbacks.onGamble!(),
      });
      this.gambleButton.visible = false;
    } else {
      this.gambleButton = null;
    }

    // Note: BalanceDisplay defaults to warm gold for the amount; for now
    // every theme inherits that. When themes diverge dramatically (e.g.
    // a high-contrast accessibility theme), the host should pass
    // theme.palette.warm via a constructor option. Wiring left as a
    // follow-up so this constructor signature stays stable.
    this.betSelector = this.buildBetSelector();
    // Producer audit Phase 4 (round 5) — INFO + SETTINGS live inside
    // ONE shared BET-pill-style capsule, not two separate disc
    // buttons. Vector-drawn icons (info circle + gear) replace the
    // Unicode glyphs ⓘ / ⚙ because the platform-font glyphs render
    // at noticeably different em-box scales (gear was ~30% smaller
    // than info-circled at the same fontSize). The custom-draw
    // shapes are sized identically so the two icons read as visual
    // siblings inside the cluster pill.
    this.infoButton = new IconButton('ⓘ', () => this.callbacks.onTogglePaytable(), {
      transparent: true,
      radius: 28,
      customDraw: drawInfoIcon,
    });
    if (callbacks.onOpenSettings) {
      this.settingsButton = new IconButton('⚙', () => this.callbacks.onOpenSettings!(), {
        transparent: true,
        radius: 28,
        customDraw: drawSettingsIcon,
      });
    } else {
      this.settingsButton = null;
    }
    this.resultLabel = this.buildResultLabel();
    // Producer brief 2026-06-04: kill the bottom-of-frame white text
    // entirely. The Text node stays in the display tree so any layout
    // / positioning code that references it (line 442 setting x/y on
    // resize) keeps working, but visible=false means nothing renders
    // — every callsite's setResultText / refreshHint becomes a silent
    // no-op on screen.
    this.resultLabel.visible = false;
    this.controlBarPlinth = new Graphics();
    this.betPillPlinth = new Graphics();

    // The plinths come FIRST in the child list so they render BEHIND
    // their associated buttons. The actual rects are drawn in
    // {@link layout} once the row positions are known.
    this.modePlateText.anchor.set(0.5);
    this.modePlateArt.anchor.set(0.5);
    this.modePlate.addChild(this.modePlateArt, this.modePlateBg, this.modePlateText);
    this.modePlate.visible = false;
    this.modePlate.eventMode = 'none';
    this.addChild(
      this.balance,
      this.freeSpinChip,
      this.controlBarPlinth,
      this.betPillPlinth,
      this.spinButton,
      this.modePlate,
      this.betSelector,
      this.infoButton,
      this.resultLabel,
    );
    if (this.gambleButton) {
      this.addChild(this.gambleButton);
    }
    if (this.settingsButton) {
      this.addChild(this.settingsButton);
    }
    if (options.showLoadoutStrip) {
      this.loadoutStrip = new LoadoutStripHud();
      this.addChild(this.loadoutStrip);
    } else {
      this.loadoutStrip = null;
    }

    // F2.2 — locked-bet sessions render the BET pill but disable
    // interaction so the player can still see the current bet (and
    // its painterly chrome reads alongside SPIN / INFO) without
    // being able to toggle away from the locked amount. Earlier
    // iterations hid it entirely, but the producer flagged the
    // missing element as visually disorienting.
    if (this.betLocked) {
      this.betSelector.setInteractive(false);
      this.betSelector.setLockBadgeVisible(true);
    }

    // Parent surface owns the wallet HUD (standalone slot's React
    // BalancePill overlay, or the wheel REEL_RACE phase's timer
    // header). Hide the balance counter unconditionally; the free-spin
    // chip stays visible when {@link keepFreeSpinChip} is set (the
    // standalone slot still needs a place to surface bonus-spin
    // counts — no other layer exposes them).
    if (options.hideHudBalance) {
      this.balance.visible = false;
      if (!options.keepFreeSpinChip) this.freeSpinChip.visible = false;
    }

    this.subscriptions.push(
      this.model.events.on('balance-changed', () => this.balance.setAmount(this.model.balance)),
      this.model.events.on('bet-changed', (event) => {
        // Selector might have come from elsewhere (e.g. clamp-to-affordable);
        // sync it back so the visible value matches the model.
        if (this.betSelector.currentBet !== event.after) {
          this.syncBetSelectorTo(event.after);
        }
        this.refreshHint();
        // Re-run the layout so the BET pill's plinth (drawn from
        // {@link BetSelector#pillWidth}) follows the new digit count.
        // Without this, only the pill's contents (digits + coin) resize
        // while the gold-bordered chrome stays at the previous width.
        if (this.lastLayoutCtx) this.layout(this.lastLayoutCtx);
      }),
      this.model.events.on('not-enough-coins', (event) => {
        this.resultLabel.text = `not enough coins for ${event.required} — pick a smaller bet`;
      }),
      this.model.events.on('free-spins-changed', (event) => {
        this.freeSpinChip.setCount(event.after);
        // Bet selector is disabled while a free-spin bonus is active —
        // changing bet mid-bonus would skew the math the trigger granted.
        // Skip when locked: the selector is hidden and re-enabling its
        // interactivity would have no visible effect anyway.
        if (!this.betLocked) {
          this.betSelector.setInteractive(event.after === 0 && !this.spinning);
        }
      }),
    );
  }

  layout(ctx: SceneLayoutContext): void {
    this.lastLayoutCtx = ctx;
    const { width, centerX, machineTop, machineLeft, machineRight, spinButtonY } = ctx;

    // Producer audit Phase 4 (slot-HUD redesign brief, round 2). The
    // persistent wallet pill (coin · gem · streak · level) lives in
    // a React DOM overlay at top-right of the canvas — see App.tsx.
    // The Pixi BalanceDisplay's position drives the take-win flying-
    // coin animation's TARGET; we anchor it directly under the COIN
    // section of the React pill so the coins appear to land into the
    // visible coin readout (NOT behind the gem button, which is
    // further to the right inside the pill).
    //
    // Pill layout (left → right inside the React pill):
    //   [18px L-pad][coin icon 20][6px gap][coin number][14px gap]
    //   [gem icon 18][6px gap][gem number][14px gap][...][18px R-pad]
    //
    // The coin number's centre sits roughly 75-95px IN from the
    // pill's left edge. The pill is right-aligned at right:18,
    // viewport-wide with content-driven width (typically ~260-300px).
    // Empirically tuned BALANCE_RIGHT_INSET = 250 puts the target
    // close to the coin numeric on a 1280-wide viewport; mobile
    // panels still land within the pill's coin section.
    //
    // The Pixi balance itself is hidden via SlotHudOptions#hideHudBalance
    // — the React pill is the visible counter. Only the position is
    // exposed (via {@link balanceLabelPosition}) for the take-win.
    //
    // Z-ORDER NOTE — the React BalancePill renders on top of the
    // canvas (DOM > canvas), so Pixi coins flying to the target are
    // VISUALLY OCCLUDED by the pill's last few pixels of approach.
    // That's by design: coins fly into the pill from below-left and
    // get absorbed at the edge, which reads as "coins landed and
    // entered the wallet".
    //
    // Vertical anchor MATH — the take-win target the host uses is
    // {@code balance.y + 16} (see BalanceDisplay#amountPosition's
    // legacy offset for centering the caption + amount as a pair).
    // The React pill sits at top:14 height:44 so its visual centre
    // is y=36. Setting balance.y = 20 makes the target = y=36 — dead
    // centre of the visible pill, so coins LAND on the coin numeric
    // rather than below the pill (which was the bug: balance.y=50
    // → target y=66 = 8px BELOW the pill).
    // Reel Race override: the multiplayer pill grows a bonus-pot cluster on
    // its LEFT and drops the streak/level chips, so the wallet coin sits much
    // closer to the right edge. The 250 above (tuned for the standalone pill,
    // where the coin is leftmost) then lands the take-win on the BONUS coin —
    // it reads as winnings flowing into the pot, not the wallet (producer
    // 2026-06-10). The loadout strip is our Reel Race signal; pull the target
    // in so the coins land on the wallet coin instead. Empirical — only the
    // gem chip sits to the coin's right here (~55px + R-pad + half the coin).
    const BALANCE_RIGHT_INSET = this.loadoutStrip ? 120 : 250;
    this.balance.x = width - BALANCE_RIGHT_INSET;
    // 2026-06-10 — the React pill is anchored at top: env(safe-area-inset
    // -top)+4, so on notch / Dynamic Island devices it sits ~59px lower
    // than on web. This balance position drives the take-win flying-coin
    // (+gem) target; shift it down by the same inset or the coins land
    // ABOVE the pill. env() reads 0 on web, so desktop is unchanged.
    this.balance.y = 20 + safeAreaInsetTop();

    // Free-spin chip: the in-bonus "N FREE SPINS" banner. Pinned
    // centred, just above the machine frame. Producer
    // 2026-06-10 — was anchored under the (hidden, top-right) balance, which
    // left it dangling at the very top of the screen.
    this.freeSpinChip.x = centerX;
    this.freeSpinChip.y = Math.max(16, machineTop - 18);

    // BET / SPIN / INFO row — spread along the slot machine's painted
    // frame per the redesign brief. BET hugs the frame's left edge,
    // SPIN sits centred (overlapping the bottom of the frame), INFO
    // hugs the frame's right edge. The auto-sized BetSelector reports
    // its rendered width via {@link BetSelector#pillWidth}; we anchor
    // it at its own centre after offsetting from the frame edge.
    //
    // On tablet/desktop the slot is centred and narrower than the
    // viewport, so machineLeft / machineRight sit well inside the
    // viewport edges — anchoring to them keeps the HUD visually
    // attached to the slot rather than floating off to the screen
    // gutters. Fallback to a viewport-edge margin when the layout
    // ctx omits the optional frame bounds (non-slot scenes shouldn't
    // mount this component, but be defensive).
    // HUD chrome scale — the spin disc, BET pill and INFO/SETTINGS
    // cluster were sized for desktop (192px disc, 64px pill height)
    // and dominated the small mobile viewport. Scale the three pieces
    // proportionally on narrow renderers so the chrome reads at the
    // same density across phones and desktops. ctx.width is in
    // renderer (canvas-internal) pixels, so DPR=2 mobile lands around
    // 1140 while a 1280 desktop sits at 2560. The mapping holds the
    // desktop look untouched (≥2000 → 1.0) and bottoms out at 0.55
    // for the smallest phones.
    const hudScale = Math.max(0.55, Math.min(1.0, width / 2000));
    const ROW_EDGE_INSET = 8;   // gap between the frame edge + pill
    const VIEWPORT_FALLBACK = 24;
    const SPIN_HALF_WIDTH = 96 * hudScale;
    // INFO + SETTINGS cluster pill — BET-pill-style capsule at 64px
    // height (matches BET so the HUD's vertical rhythm stays even).
    // The two glyph icons sit inside this single capsule (no per-icon
    // borders). All dimensions are HUD-scaled so the cluster shrinks
    // in step with SPIN + BET on narrow viewports.
    const CLUSTER_PILL_HEIGHT = 64 * hudScale;
    const CLUSTER_ICON_RADIUS = 28 * hudScale;
    /** Gap between adjacent icon edges (visual breathing room). */
    const CLUSTER_ICON_EDGE_GAP = 10 * hudScale;
    /** Padding from the pill's inner edge to the outermost icon edge. */
    const CLUSTER_INNER_PAD_X = 16 * hudScale;
    /** Centre-to-centre distance between the two icons inside the
     *  cluster pill. */
    const iconCenterToCenter =
      CLUSTER_ICON_RADIUS * 2 + CLUSTER_ICON_EDGE_GAP;
    const clusterIconCount = this.settingsButton ? 2 : 1;
    /** Span from the leftmost icon's left edge to the rightmost
     *  icon's right edge (used to size the pill). */
    const clusterContentW =
      clusterIconCount === 2
        ? iconCenterToCenter + CLUSTER_ICON_RADIUS * 2
        : CLUSTER_ICON_RADIUS * 2;
    const clusterPillW = clusterContentW + CLUSTER_INNER_PAD_X * 2;
    // Outer-half-width used by the right-anchor math below — what the
    // layout treats as the "INFO" footprint is now the cluster pill's
    // half-width, not a single button's radius.
    const INFO_HALF_WIDTH = clusterPillW / 2;
    /** SPIN lifts above the BET/INFO row so the larger disc visibly
     *  bites into the wood frame's bottom edge instead of merely
     *  touching it. Empirically tuned against the painted slot frames
     *  — ~half the size-growth (from 128→192) reads as "pressed into
     *  the frame" without floating SPIN off the action row. Scales
     *  with the rest of the HUD chrome so the lift stays proportional
     *  to the disc size. */
    const SPIN_LIFT = 32 * hudScale;
    // BET pill's intrinsic half-width — multiplied by hudScale because
    // the pill itself is scaled below.
    const betHalf = (this.betSelector.pillWidth / 2) * hudScale;

    const leftAnchor =
      machineLeft !== undefined ? machineLeft : VIEWPORT_FALLBACK;
    const rightAnchor =
      machineRight !== undefined ? machineRight : width - VIEWPORT_FALLBACK;

    // Apply the HUD scale to each chrome widget. Scaling the
    // Container shrinks the visual + hit area symmetrically around
    // the widget's origin (PrimaryButton + IconButton both draw
    // centred at 0,0; BetSelector mirrors that), so the anchored
    // positions below remain visually accurate.
    this.spinButton.scale.set(hudScale);
    this.betSelector.scale.set(hudScale);
    this.infoButton.scale.set(hudScale);
    if (this.settingsButton) this.settingsButton.scale.set(hudScale);

    this.spinButton.x = centerX;
    this.spinButton.y = spinButtonY - SPIN_LIFT;
    this.modePlate.scale.set(hudScale);
    this.modePlate.x = this.spinButton.x;
    this.modePlate.y = this.spinButton.y;
    // GAMBLE medallion. On a wide action row it tucks just to the right
    // of SPIN (the designed look). On narrow (mobile) viewports the row
    // can't fit BET · SPIN · GAMBLE · gear/info cluster side-by-side, and
    // the unclamped position rode on top of the SETTINGS glyph — so when
    // there isn't room before the cluster, stack the medallion just ABOVE
    // the cluster pill instead of overlapping it.
    if (this.gambleButton) {
      this.gambleButton.scale.set(hudScale);
      const spinHalfScaled = 96 * hudScale;
      const gambleHalfScaled = 48 * hudScale;
      const gambleGap = 12 * hudScale;
      const clusterLeftEdge = rightAnchor - ROW_EDGE_INSET - clusterPillW;
      // GAMBLE always sits to the RIGHT of SPIN, on the action row (the
      // desktop look). Tuck it just past the disc; but never let it ride
      // onto the INFO/SETTINGS cluster — clamp its centre so its right edge
      // stays left of the cluster. On a wide row the clamp is a no-op
      // (tucked beside SPIN); on a narrow mobile row it slides left to abut
      // the cluster rather than jumping ABOVE it, which read as a stray
      // floating button (producer 2026-06-10).
      const tuckedX = centerX + spinHalfScaled + gambleGap + gambleHalfScaled;
      const maxX = clusterLeftEdge - gambleGap - gambleHalfScaled;
      this.gambleButton.x = Math.min(tuckedX, maxX);
      this.gambleButton.y = spinButtonY - SPIN_LIFT;
    }
    this.betSelector.x = leftAnchor + ROW_EDGE_INSET + betHalf;
    this.betSelector.y = spinButtonY;

    // Cluster pill centre — anchored to the slot frame's right edge
    // via INFO_HALF_WIDTH (= cluster pill half-width). The two glyph
    // icons inside the cluster sit at ± iconCenterToCenter/2 offsets
    // from this centre when both are present; single icon centred
    // when only INFO exists.
    const clusterCenterX = rightAnchor - ROW_EDGE_INSET - INFO_HALF_WIDTH;
    if (this.settingsButton) {
      // Settings glyph on the LEFT, INFO glyph on the RIGHT.
      this.settingsButton.x = clusterCenterX - iconCenterToCenter / 2;
      this.settingsButton.y = spinButtonY;
      this.infoButton.x = clusterCenterX + iconCenterToCenter / 2;
      this.infoButton.y = spinButtonY;
    } else {
      this.infoButton.x = clusterCenterX;
      this.infoButton.y = spinButtonY;
    }

    // Hint sits beneath the SPIN button. SPIN's new lower edge is at
    // (spinButtonY - SPIN_LIFT + 96); the hint sits a touch below
    // that so it doesn't collide.
    this.resultLabel.x = centerX;
    this.resultLabel.y = spinButtonY - SPIN_LIFT + 96 + 14;

    // Re-anchor the locked-bet tooltip above the (now-positioned) BET
    // pill. No-op until the tooltip's been built (first locked tap).
    this.positionLockedBetTooltip(ctx);

    // Cluster pill — drawn in the EXACT BET-pill style so the two
    // visually read as siblings (same height, same gold trim, same
    // navy body, same drop-shadow + top highlight + inner glow). The
    // INFO + SETTINGS glyphs sit inside this one capsule rather than
    // each carrying its own disc (transparent: true on each icon).
    this.controlBarPlinth.clear();
    const pillHalfW = clusterPillW / 2;
    const pillHalfH = CLUSTER_PILL_HEIGHT / 2;
    const pillRadius = pillHalfH; // perfect-pill corner
    this.controlBarPlinth
      // Drop-shadow ground (offset, low alpha)
      .roundRect(-pillHalfW + 2, -pillHalfH + 4, clusterPillW, CLUSTER_PILL_HEIGHT, pillRadius)
      .fill({ color: 0x000000, alpha: 0.45 })
      // Navy body
      .roundRect(-pillHalfW, -pillHalfH, clusterPillW, CLUSTER_PILL_HEIGHT, pillRadius)
      .fill({ color: 0x16113d, alpha: 1 })
      // Top highlight band
      .roundRect(-pillHalfW + 4, -pillHalfH + 3, clusterPillW - 8, CLUSTER_PILL_HEIGHT / 2 - 4, pillRadius)
      .fill({ color: 0x2a1e63, alpha: 0.55 })
      // Gold outer border
      .roundRect(-pillHalfW, -pillHalfH, clusterPillW, CLUSTER_PILL_HEIGHT, pillRadius)
      .stroke({ color: 0xffc940, alpha: 1, width: 3 })
      // Inner soft-gold glow
      .roundRect(-pillHalfW + 3, -pillHalfH + 3, clusterPillW - 6, CLUSTER_PILL_HEIGHT - 6, pillRadius - 3)
      .stroke({ color: 0xffe28a, alpha: 0.35, width: 1 });
    this.controlBarPlinth.x = clusterCenterX;
    this.controlBarPlinth.y = spinButtonY;

    // Bet pill plinth — drawn here at WORLD scale with the same
    // gradient + gold stroke recipe as the cluster pill above. The
    // BetSelector itself only renders the digit + coin glyph; this
    // plinth provides the chrome. Producer brief 2026-05-12: "if
    // you are [matching the cluster], then don't use a background
    // — instead replicate the hud from a rounded rectangle with
    // gold stroke and the same blue black gradient."
    const betPillW = this.betSelector.pillWidth * hudScale;
    const betPillH = BetSelector.PILL_HEIGHT * hudScale;
    const betHalfW = betPillW / 2;
    const betHalfH = betPillH / 2;
    const betRadius = betHalfH;
    this.betPillPlinth.clear();
    this.betPillPlinth
      // Drop-shadow ground (offset, low alpha)
      .roundRect(-betHalfW + 2, -betHalfH + 4, betPillW, betPillH, betRadius)
      .fill({ color: 0x000000, alpha: 0.45 })
      // Navy body
      .roundRect(-betHalfW, -betHalfH, betPillW, betPillH, betRadius)
      .fill({ color: 0x16113d, alpha: 1 })
      // Top highlight band
      .roundRect(-betHalfW + 4, -betHalfH + 3, betPillW - 8, betPillH / 2 - 4, betRadius)
      .fill({ color: 0x2a1e63, alpha: 0.55 })
      // Gold outer border
      .roundRect(-betHalfW, -betHalfH, betPillW, betPillH, betRadius)
      .stroke({ color: 0xffc940, alpha: 1, width: 3 })
      // Inner soft-gold glow
      .roundRect(-betHalfW + 3, -betHalfH + 3, betPillW - 6, betPillH - 6, betRadius - 3)
      .stroke({ color: 0xffe28a, alpha: 0.35, width: 1 });
    this.betPillPlinth.x = this.betSelector.x;
    this.betPillPlinth.y = this.betSelector.y;
    // The free stretch of the action row between BET and SPIN (the side
    // Gamble never takes), for the relic shelf.
    this.actionGapNow = {
      left: this.betSelector.x + betHalfW,
      right: this.spinButton.x - SPIN_HALF_WIDTH,
      y: spinButtonY,
      height: betPillH,
    };

    // Silence unused-var lints when SPIN_HALF_WIDTH isn't referenced
    // again — kept as a named constant for the next caller wiring
    // collision math (e.g. drag-aware tooltips) against the disc.
    void SPIN_HALF_WIDTH;

    // Optional loadout strip — multiplayer Reel Race "special items".
    // Producer 2026-05-29: moved OUT of the bottom BET/SPIN/INFO row
    // (where the three slots crowded the SPIN disc) up to the sky band
    // above the frame, right-aligned. machineTop is the frame's top
    // edge; fall back to a top-of-canvas band when the frame bounds
    // aren't supplied (defensive — the reel-race host always supplies
    // them).
    if (this.loadoutStrip) {
      this.loadoutStrip.layoutTopRight({
        rightAnchor,
        frameTopY: machineTop !== undefined ? machineTop : 120,
        edgeInset: ROW_EDGE_INSET,
        hudScale,
      });
    }
  }

  destroy(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
    if (this.lockedHintTimer !== null) {
      clearTimeout(this.lockedHintTimer);
      this.lockedHintTimer = null;
    }
    this.balance.destroyDisplay();
    super.destroy({ children: true });
  }

  // -------- public API used by the host scene --------

  /** Show the spinning state — disables bet cycling and HIDES the
   * spin button until the spin completes. Producer brief 2026-05-12:
   * on mobile the spin button was overlapping the bonus-coin collect
   * animation, hiding the "+N" payout that lifts from the reels.
   * Hiding the button entirely while spinning solves both the visual
   * conflict and the dead-pixel space (the player can't tap mid-spin
   * anyway). Bet selector stays disabled while free spins are banked,
   * regardless of {@code spinning}. */
  setSpinning(spinning: boolean): void {
    this.spinning = spinning;
    this.spinButton.setLabel(spinning ? '…' : 'SPIN');
    this.spinButton.setSuppressHover(spinning);
    this.spinButton.visible = !spinning;
    if (!this.betLocked) {
      const allowBet = !spinning && this.model.freeSpinsRemaining === 0;
      this.betSelector.setInteractive(allowBet);
    }
  }

  setSpinButtonVisible(visible: boolean): void {
    this.spinButton.visible = visible;
  }

  /** Royal Road: what SPIN means right now. During a Lamp Run the button
   *  reads STOP, a dimmed "…" while the light lands, then COLLECT (or DONE
   *  for an empty pot); 'spin' is the normal button. */
  setSpinMode(mode: SpinMode): void {
    this.spinMode = mode;
    const plate = PLATES[mode];
    this.modePlate.visible = plate !== null;
    if (mode !== 'spin') {
      this.spinButton.visible = true;
      this.spinButton.setSuppressHover(mode === 'landing');
    }
    this.spinButton.alpha = mode === 'landing' ? 0.6 : 1;
    if (!plate) return;
    // The button art covers SPIN exactly: same size, same disc.
    const art = AssetLoader.shared.getTexture(plate.art);
    this.modePlateArt.visible = art !== null;
    this.modePlateBg.visible = art === null;
    this.modePlateText.visible = art === null;
    if (art) {
      this.modePlateArt.texture = art;
      this.modePlateArt.width = SPIN_SIZE;
      this.modePlateArt.height = SPIN_SIZE;
      this.modePlateArt.tint = mode === 'landing' ? 0x9a90a8 : 0xffffff;
      return;
    }
    // Without the art: a round face that fits inside the SPIN disc's white
    // rim, so the button keeps its shape and only its colour and word change.
    this.modePlateText.text = plate.label;
    this.modePlateText.scale.set(1);
    const fit = FACE_LABEL_WIDTH / Math.max(1, this.modePlateText.width);
    this.modePlateText.scale.set(Math.min(1, fit));
    this.modePlateText.y = 2;
    this.modePlateBg
      .clear()
      .circle(0, 0, FACE_RADIUS)
      .fill({ color: plate.fill })
      .ellipse(0, -FACE_RADIUS * 0.42, FACE_RADIUS * 0.72, FACE_RADIUS * 0.4)
      .fill({ color: plate.light, alpha: 0.55 })
      .circle(0, 0, FACE_RADIUS)
      .stroke({ color: 0x000000, alpha: 0.18, width: 3 });
  }

  get currentSpinMode(): SpinMode {
    return this.spinMode;
  }

  /** Show / hide the inline GAMBLE medallion. No-op when the host
   *  didn't provide an {@code onGamble} callback at construction
   *  (e.g. multiplayer Reel Race scene which doesn't offer gambling). */
  setGambleButtonVisible(visible: boolean): void {
    if (this.gambleButton) this.gambleButton.visible = visible;
  }

  setResultText(text: string): void {
    this.resultLabel.text = text;
  }

  /** Restore the default "bet X · SPIN or space..." hint text. No-op while
   * spinning so we don't blow away a "spinning…" message mid-spin. */
  refreshHint(): void {
    if (this.spinning) return;
    this.resultLabel.text = HINT_TEMPLATE(this.model.currentBet);
  }

  setMaxAffordable(max: number): void {
    this.betSelector.setMaxAffordable(max);
  }

  /** Pulse on the balance counter as each coin lands during the take-win
   * sequence. */
  bumpBalance(): void {
    this.balance.bump();
  }

  /** Live coin / gem chip centres reported by the React BalancePill (via
   *  {@link setWalletAnchors}). When present they OVERRIDE the computed
   *  take-win targets so coins + gems fly to the pill's ACTUAL on-screen
   *  chips, instead of a hardcoded offset that drifts whenever the pill's
   *  layout changes (streak/level chips toggling, abbreviation width, the
   *  bonus cluster). Stage-local px == viewport CSS px here, since the
   *  canvas fills the screen at inset:0. Producer 2026-06-10 — the
   *  "position bridge" that retires the BALANCE_RIGHT_INSET guess. */
  private coinAnchorOverride: { x: number; y: number } | null = null;
  private gemAnchorOverride: { x: number; y: number } | null = null;

  /** Feed the live coin + gem chip centres (viewport CSS px) measured on
   *  the React pill. Pass null to fall back to the computed positions
   *  (e.g. the pill isn't mounted, or in tests). */
  setWalletAnchors(
    anchors:
      | { coin: { x: number; y: number }; gem: { x: number; y: number } }
      | null,
  ): void {
    this.coinAnchorOverride = anchors ? anchors.coin : null;
    this.gemAnchorOverride = anchors ? anchors.gem : null;
  }

  /** Take-win target for coins — the live coin-chip centre when the React
   *  pill has reported it, else the stage-local balance amount label. */
  private actionGapNow: { left: number; right: number; y: number; height: number } | null = null;

  /** The action row between the BET pill's right edge and SPIN's left
   *  edge, at the row's centre line (null before the first layout). */
  get actionGap(): { left: number; right: number; y: number; height: number } | null {
    return this.actionGapNow;
  }

  get balanceLabelPosition(): { x: number; y: number } {
    return this.coinAnchorOverride ?? this.balance.amountPosition;
  }

  /** Take-win target for gems — the live gem-chip centre when reported,
   *  else a hardcoded ~85px offset from the coin (right-anchored pill,
   *  same y). */
  get gemLabelPosition(): { x: number; y: number } {
    if (this.gemAnchorOverride) return this.gemAnchorOverride;
    const coin = this.balance.amountPosition;
    return { x: coin.x + 85, y: coin.y };
  }

  // -------- private builders --------

  private buildBetSelector(): BetSelector {
    const selector = new BetSelector(
      [...this.model.availableBetAmounts],
      this.model.currentBet,
    );
    selector.onBetChange = (bet) => {
      this.model.setBet(bet);
      this.callbacks.onBetChosen?.(bet);
    };
    // Tapping a LOCKED bet pill flashes an explainer in the result
    // label instead of dead-ending. The bet locks in two contexts —
    // the multiplayer Reel Race (every seat spins at the same notional
    // bet) and onboarding session 1 — so the copy names the
    // multiplayer reason, which is the dominant + permanent case. The
    // label auto-clears after a beat so it doesn't linger over the
    // SPIN button.
    // 2026-06-04 — only surface the "Locked for multiplayer"
    // explainer when the bet is PERMANENTLY locked (multiplayer Reel
    // Race / onboarding session 1). When the selector is transiently
    // disabled because a spin is in progress or free-spins are
    // banked, taps should silently no-op — the player isn't blocked
    // from changing the bet "for multiplayer reasons" in those cases,
    // they just need to wait for the current spin to resolve.
    // Without this guard, the tooltip read as a serious mode
    // mismatch ("locked for multiplayer" in a single-player slot)
    // when really it was just a transient disable race.
    selector.onLockedTap = () => {
      if (this.betLocked) this.showLockedBetTooltip();
    };
    return selector;
  }

  /** Auto-hide timer for the locked-bet tooltip. Cleared + reset on
   *  each tap so rapid taps don't stack timers. */
  private lockedHintTimer: number | null = null;

  /** Show the "Locked for multiplayer" tooltip bubble above the BET
   *  pill for ~2.2s. Built on first call. Positioned in {@link layout};
   *  if layout hasn't run yet (shouldn't happen — the scene lays out
   *  before it's interactive) the bubble still shows at its last known
   *  position. */
  private showLockedBetTooltip(): void {
    if (this.lockedBetTooltip === null) {
      this.lockedBetTooltip = this.buildLockedBetTooltip();
      this.addChild(this.lockedBetTooltip);
      // Position immediately if we already have a layout context.
      if (this.lastLayoutCtx) this.positionLockedBetTooltip(this.lastLayoutCtx);
    }
    this.lockedBetTooltip.visible = true;
    this.lockedBetTooltip.alpha = 1;
    if (this.lockedHintTimer !== null) {
      clearTimeout(this.lockedHintTimer);
    }
    this.lockedHintTimer = setTimeout(() => {
      this.lockedHintTimer = null;
      if (this.lockedBetTooltip) this.lockedBetTooltip.visible = false;
    }, 2200) as unknown as number;
  }

  /** Build the tooltip bubble — a navy rounded-rect with a gold border
   *  + lock-prefixed label, anchored at its bottom-centre so
   *  {@link positionLockedBetTooltip} can sit it just above the pill.
   *  A small downward nub points at the pill. */
  private buildLockedBetTooltip(): Container {
    const c = new Container();
    const label = new Text({
      text: '🔒 Locked for multiplayer',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 15,
        fontWeight: '700',
        fill: 0xffe28a,
      },
    });
    label.anchor.set(0.5, 0.5);
    const padX = 16;
    const padY = 9;
    const w = label.width + padX * 2;
    const h = label.height + padY * 2;
    const nub = 7;
    const bg = new Graphics();
    // Bubble body — bottom edge sits at y=0 so the container's origin
    // is the bubble's bottom-centre (where the nub points down).
    bg.roundRect(-w / 2, -h - nub, w, h, 12)
      .fill({ color: 0x1a0830 })
      .stroke({ color: 0xffc940, width: 2, alignment: 0 });
    // Downward nub — small triangle from the bubble's bottom edge to
    // the origin point (which we'll position at the pill's top).
    bg.moveTo(-nub, -nub)
      .lineTo(nub, -nub)
      .lineTo(0, 0)
      .closePath()
      .fill({ color: 0x1a0830 });
    label.y = -nub - h / 2;
    c.addChild(bg, label);
    c.visible = false;
    return c;
  }

  /** Position the tooltip so its nub points at the top-centre of the
   *  BET pill. Called from {@link layout} (recomputes on every relayout)
   *  + once on first build. */
  private positionLockedBetTooltip(ctx: SceneLayoutContext): void {
    if (!this.lockedBetTooltip) return;
    // Same mapping as layout()'s hudScale (kept in sync by hand — both
    // derive from ctx.width). Renderer-px width → [0.55, 1.0].
    const hudScale = Math.max(0.55, Math.min(1.0, ctx.width / 2000));
    this.lockedBetTooltip.scale.set(hudScale);
    // Bet pill top edge = betSelector.y - (PILL_HEIGHT/2)*scale. Nub
    // origin sits a few px above that so it visually points at the pill.
    const pillTopY =
      this.betSelector.y - (BetSelector.PILL_HEIGHT / 2) * hudScale - 4 * hudScale;
    this.lockedBetTooltip.x = this.betSelector.x;
    this.lockedBetTooltip.y = pillTopY;
  }

  private buildResultLabel(): Text {
    const t = new Text({
      text: HINT_TEMPLATE(this.model.currentBet),
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 13,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'center',
      },
    });
    t.anchor.set(0.5);
    t.alpha = 0.7;
    return t;
  }

  private syncBetSelectorTo(amount: number): void {
    // Select directly: cycling is ignored while the pill is locked (a first
    // session, or a Royal Road slot's fixed bet), which left it showing the
    // old bet.
    this.betSelector.select(amount);
  }
}

// ---------------------------------------------------------------------
// Vector-drawn cluster icons (info + gear). Producer audit Phase 4 —
// the Unicode glyphs (ⓘ, ⚙) rendered at significantly different
// em-box scales in the platform font (gear was ~30% smaller than the
// info-circled glyph at the same fontSize). These hand-drawn shapes
// keep the two icons identically sized + balanced inside the cluster
// pill. Both are sized in stroke widths relative to the requested
// {@code size}, so they scale cleanly if the cluster pill's height
// changes.
// ---------------------------------------------------------------------

/** Info icon — circled "i": outer ring + dot + stem. Tinted the same
 *  warm gold as the BET number ({@code 0xFFC940}) so the cluster reads
 *  as a colour family with the bet selector instead of fighting it
 *  with bare white. */
function drawInfoIcon(container: Container, size: number): void {
  const r = size / 2;
  const stroke = Math.max(2, size / 12);
  const g = new Graphics();
  // Outer ring
  g.circle(0, 0, r - stroke / 2).stroke({ color: 0xffc940, width: stroke });
  // Dot (top of the "i")
  g.circle(0, -r * 0.42, stroke * 0.95).fill({ color: 0xffc940 });
  // Stem (vertical bar of the "i")
  g.roundRect(-stroke * 0.55, -r * 0.12, stroke * 1.1, r * 0.66, stroke * 0.55)
    .fill({ color: 0xffc940 });
  container.addChild(g);
}

/** Settings icon — 8-tooth gear with centre cutout. The cutout uses
 *  the cluster-pill body colour so the gear reads as a notched ring
 *  against the pill rather than a filled disc. */
function drawSettingsIcon(container: Container, size: number): void {
  const r = size / 2;
  // Build an 8-tooth gear via an alternating outer/inner polygon. We
  // use 32 points (8 teeth × 4 vertices each: two outer corners +
  // two inner corners) so the teeth read crisply without softening.
  const teeth = 8;
  const outerR = r;
  const innerR = r * 0.74;
  // Each tooth spans 1/teeth of the circle; the tooth's outer arc
  // covers about 55% of that slice, the valley between teeth the
  // remaining 45%. Tunable for narrower / wider teeth.
  const toothFraction = 0.55;
  const points: number[] = [];
  for (let i = 0; i < teeth; i++) {
    const sliceStart = (i / teeth) * Math.PI * 2 - Math.PI / 2;
    const sliceEnd = ((i + 1) / teeth) * Math.PI * 2 - Math.PI / 2;
    const toothEnd = sliceStart + (sliceEnd - sliceStart) * toothFraction;
    // Outer-outer-inner-inner per tooth slice.
    points.push(Math.cos(sliceStart) * outerR, Math.sin(sliceStart) * outerR);
    points.push(Math.cos(toothEnd) * outerR, Math.sin(toothEnd) * outerR);
    points.push(Math.cos(toothEnd) * innerR, Math.sin(toothEnd) * innerR);
    points.push(Math.cos(sliceEnd) * innerR, Math.sin(sliceEnd) * innerR);
  }
  const g = new Graphics();
  // Tinted the same warm gold as the BET number ({@code 0xFFC940})
  // and the info icon, so the cluster reads as a coherent colour
  // family with the bet selector rather than bare white.
  g.poly(points).fill({ color: 0xffc940 });
  // Centre hole — uses the cluster pill's body colour so the gear
  // reads as a ring/donut. Hard-coded because the pill colour is
  // fixed in this component; if the pill ever becomes themeable,
  // pass the colour as a draw-fn parameter.
  g.circle(0, 0, r * 0.32).fill({ color: 0x16113d });
  container.addChild(g);
}
